import { forwardRef, useContext, useMemo } from 'react';
import { useTheme } from '@trinity/chat-components/components/Theme/index.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming';
import { cx } from '@trinity/chat-components/utils';
import type { ButtonProps } from './interfaces';
import styles from './style.module.css';

type Props = ButtonProps;

const ButtonBase = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const theming = useContext(AppThemingContext);
  const theme = useTheme();
  const attributes = extractHTMLAttributes(props);
  const className = useResolveButtonClass(props);

  // `themeColor` (when given) takes precedence over AppTheming/the static CSS-variable
  // fallback, so a caller can opt a single button into tracking the live MUI theme.
  const themeColorMain = props.themeColor && theme.palette[props.themeColor].main;
  const themeColorHover = props.themeColor === 'primary' ? theme.palette.primary.dark1 : themeColorMain;

  return (
    <button
      className={className}
      ref={ref}
      {...attributes}
      style={{
        '--theme-color': themeColorMain || (theming?.enabled ? theming?.palette?.primary?.main : 'var(--pri-02)'),
        '--theme-color-hover': themeColorHover || (theming?.enabled ? theming?.palette?.primary?.dark : 'var(--sec-02)'),
      } as React.CSSProperties}
    >
      {props.title || props.children}
    </button>
  );
});

type CSSProps = Pick<
  ButtonProps,
  'className' | 'color' | 'implicitDisable' | 'size' | 'variant'
>;

const useResolveButtonClass = (props: CSSProps) => {
  return useMemo(() => {
    const colorStyles =
      {
        affirmative: styles.affirmative,
        destructive: styles.destructive,
        primary: styles.primary,
        secondary: styles.secondary,
        transparent: styles.transparent,
        delete: styles.delete,
      }[props.color] ||
      cx(styles.primary, {
        [styles.transparent]: props.variant === 'link',
      });

    const variantStyles =
      {
        brick: styles.brick,
        pill: styles.pill,
        link: styles.link,
        text: styles.text,
      }[props.variant] || styles.pill;

    const implicitStyles = {
      [styles.implicit]: props.implicitDisable,
    };

    const sizeStyles = {
      small: styles.smallSize,
    }[props.size];

    return cx(
      colorStyles,
      variantStyles,
      implicitStyles,
      props.className,
      sizeStyles,
    );
  }, [props]);
};

function extractHTMLAttributes(
  props: Props,
): React.ButtonHTMLAttributes<HTMLButtonElement> {
  const {
    children,
    className,
    color,
    implicitDisable,
    variant,
    title,
    themeColor,
    ...other
  } = props;

  return other;
}

export default ButtonBase;
